export type MediaAssetKind = "image" | "video";

export interface MinimalArtifactForNaming {
  name: string;
  subject?: string | null;
  metadata?: Record<string, unknown> | null;
}

export interface ReferenceImageForNaming {
  url?: string;
  productTitle?: string;
  kind?: string;
  elementKey?: string;
  reuseCount?: number;
  assignedBeat?: string;
  ugcSceneSlot?: string;
  sourceVideoScriptAngle?: string;
}

const GENERATED_ID_PATTERNS = [
  /\b[a-z]{1,8}[_-]\d{8,}(?:[_-][a-z0-9]+)*\b/gi,
  /\b\d{10,}(?:[_-]\d+)*\b/g,
  /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi,
  /\bc[a-z0-9]{20,}\b/gi,
];

const UPPERCASE_WORDS = new Map<string, string>([
  ["ai", "AI"],
  ["api", "API"],
  ["cta", "CTA"],
  ["id", "ID"],
  ["seo", "SEO"],
  ["ugc", "UGC"],
  ["url", "URL"],
]);

function stripGeneratedIds(value: string): string {
  let next = value;
  for (const pattern of GENERATED_ID_PATTERNS) {
    next = next.replace(pattern, " ");
  }
  return next;
}

function normalizeTitleSeparators(value: string): string {
  return value
    .replace(/[\u2013\u2014]/g, " - ")
    .replace(/\s*[-:|]\s*/g, " - ")
    .replace(/\s+/g, " ")
    .replace(/(?:\s-\s){2,}/g, " - ")
    .trim();
}

function titleCaseWord(word: string): string {
  const lower = word.toLowerCase();
  const mapped = UPPERCASE_WORDS.get(lower);
  if (mapped) return mapped;
  if (/^v\d+$/i.test(word)) return `V${word.slice(1)}`;
  if (/^\d+[a-z]+$/i.test(word)) return lower;
  if (word.length <= 1) return word.toUpperCase();
  return `${word.charAt(0).toUpperCase()}${word.slice(1).toLowerCase()}`;
}

function limitTitle(value: string, max = 80): string {
  if (value.length <= max) return value;
  const sliced = value
    .slice(0, max)
    .replace(/\s+\S*$/, "")
    .trim();
  return sliced || value.slice(0, max).trim();
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;
}

function pickString(
  record: Record<string, unknown> | null | undefined,
  keys: readonly string[],
): string | undefined {
  for (const key of keys) {
    const value = record?.[key];
    if (typeof value === "string" && value.trim().length > 0) {
      return value.trim();
    }
  }
  return undefined;
}

function pickNumber(
  record: Record<string, unknown> | null | undefined,
  keys: readonly string[],
): number | undefined {
  for (const key of keys) {
    const value = record?.[key];
    if (typeof value === "number" && Number.isFinite(value)) return value;
  }
  return undefined;
}

export function humanizeAssetText(value: string | null | undefined): string {
  if (!value) return "";
  let text = value.trim();
  try {
    text = decodeURIComponent(text);
  } catch {
    // Keep the raw value when it is not URL-encoded text.
  }
  text = stripGeneratedIds(text)
    .replace(/\.[a-z0-9]{2,5}$/i, "")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_./+]+/g, " ")
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!text) return "";
  return text.split(" ").map(titleCaseWord).join(" ");
}

function cleanReadableTitle(value: string | null | undefined): string {
  if (!value) return "";
  const withoutIds = stripGeneratedIds(value);
  const normalized = normalizeTitleSeparators(withoutIds);
  const parts = normalized
    .split(/\s-\s/g)
    .map((part) => humanizeAssetText(part))
    .filter(Boolean);
  return limitTitle(parts.join(" - "));
}

function joinTitleParts(parts: Array<string | null | undefined>): string {
  return parts
    .map((part) => cleanReadableTitle(part))
    .filter(Boolean)
    .join(" - ");
}

function appendSuffixIfMissing(base: string, suffix: string): string {
  if (!base) return suffix;
  return new RegExp(`\\b${suffix}\\b`, "i").test(base) ? base : `${base} ${suffix}`;
}

function moveLeadingWordToEnd(base: string, word: string): string {
  const pattern = new RegExp(`^${word}\\s+(.+)$`, "i");
  const match = base.match(pattern);
  return match?.[1] ? `${match[1]} ${titleCaseWord(word)}` : base;
}

function titleForElementReference(
  elementKey: string | undefined,
  kind: string | undefined,
): string {
  const normalizedKind = (kind ?? "").replace(/^ugc-element-/, "");
  let base = humanizeAssetText(elementKey) || humanizeAssetText(normalizedKind);

  if (!normalizedKind && /^Pose\s+/i.test(base)) {
    base = moveLeadingWordToEnd(base, "Pose");
    return appendSuffixIfMissing(base, "Pose");
  }

  if (
    normalizedKind === "background" ||
    normalizedKind === "bg" ||
    normalizedKind === "ugc-element-bg"
  ) {
    base = base.replace(/^Bg\s+/i, "");
    return appendSuffixIfMissing(base, "Background");
  }

  if (normalizedKind === "avatar-pose" || normalizedKind === "ugc-avatar-pose") {
    base = moveLeadingWordToEnd(base, "Pose");
    return appendSuffixIfMissing(base, "Pose");
  }

  if (normalizedKind === "item" || normalizedKind === "ugc-element-item") {
    return appendSuffixIfMissing(base, "Prop");
  }

  return base || "Reference Image";
}

export function readableReferenceTitle(
  image: ReferenceImageForNaming,
  options: { includeReuse?: boolean } = {},
): string {
  if (image.kind === "product-photo") {
    return cleanReadableTitle(image.productTitle) || "Product Photo";
  }

  if (
    image.kind === "ugc-element-product" ||
    image.kind === "ugc-element-item" ||
    image.kind === "ugc-element-bg" ||
    image.kind === "ugc-avatar-pose"
  ) {
    const kindMap: Record<string, string> = {
      "ugc-element-product": "product",
      "ugc-element-item": "item",
      "ugc-element-bg": "background",
      "ugc-avatar-pose": "avatar-pose",
    };
    const base = titleForElementReference(image.elementKey, kindMap[image.kind]);
    const reuse =
      options.includeReuse && image.reuseCount && image.reuseCount > 1
        ? ` x${image.reuseCount}`
        : "";
    return `${base}${reuse}`;
  }

  if (image.kind === "ugc-text-frame") {
    return image.assignedBeat
      ? `${humanizeAssetText(image.assignedBeat)} Text Frame`
      : "Text Frame";
  }

  if (image.kind === "ugc-scene-ref") {
    const slot = humanizeAssetText(image.ugcSceneSlot) || "Scene";
    const angle = cleanReadableTitle(image.sourceVideoScriptAngle);
    return angle ? `${slot} - ${angle}` : slot;
  }

  return cleanReadableTitle(image.productTitle) || "Reference Image";
}

function artifactTitleFromRawName(name: string, subject?: string | null): string {
  const hadEdit = /\bedit(?:ed)?\b/i.test(name);
  const cleanedName = name.replace(/\bedit(?:ed)?\b/gi, " ");
  const readable = cleanReadableTitle(cleanedName);
  const compactReadable = readable.replace(/\s-\s/g, " ");
  const adMatch = compactReadable.match(/^Ad\s+(Image|Video)(?:\s+(.+))?$/i);

  let base = readable;
  if (adMatch) {
    const mediaWord = titleCaseWord(adMatch[1] ?? (subject?.includes("video") ? "video" : "image"));
    const label = cleanReadableTitle(adMatch[2] ?? "");
    base = label && !/^Generated$/i.test(label) ? `${label} ${mediaWord}` : `${mediaWord} Ad`;
  } else if (subject === "ugc-portrait") {
    base = appendSuffixIfMissing(readable.replace(/^UGC Portrait\s+/i, ""), "UGC Portrait");
  } else if (subject === "ugc-element") {
    base = titleForElementReference(name, undefined);
  }

  if (hadEdit) base = appendSuffixIfMissing(base, "Edit");
  return limitTitle(base || "Asset");
}

export function buildAdArtifactDisplayName(input: {
  adType: MediaAssetKind;
  label?: string;
  metadata?: Record<string, unknown> | null;
  fallbackTitle?: string;
}): string {
  const meta = input.metadata ?? undefined;
  const explicit = pickString(meta, ["displayName", "display_name", "assetTitle"]);
  if (explicit) return cleanReadableTitle(explicit);

  const productName = pickString(meta, ["productName", "product_name"]);
  const templateName = pickString(meta, ["templateName", "template_name"]);
  const angle = pickString(meta, ["sourceResearchAngle", "researchAngle", "scriptAngle"]);
  const target = pickString(meta, ["targetProblem", "overlayText"]);
  const batchIndex = pickNumber(meta, ["batchIndex"]);
  const format = input.adType === "video" ? "Video Ad" : "Image Ad";

  let base =
    joinTitleParts([productName, templateName]) ||
    joinTitleParts([productName, angle]) ||
    joinTitleParts([productName, target]) ||
    (productName ? `${cleanReadableTitle(productName)} ${format}` : "") ||
    (templateName ? `${cleanReadableTitle(templateName)} ${format}` : "") ||
    (input.label
      ? `${cleanReadableTitle(input.label)} ${input.adType === "video" ? "Video" : "Image"}`
      : "") ||
    input.fallbackTitle ||
    format;

  if (batchIndex != null && !/\bvariant\s+\d+\b/i.test(base)) {
    base = joinTitleParts([base, `Variant ${batchIndex + 1}`]);
  }

  return limitTitle(base);
}

export function readableArtifactTitle(artifact: MinimalArtifactForNaming): string {
  const meta = artifact.metadata ?? undefined;
  const explicit = pickString(meta, ["displayName", "display_name", "assetTitle"]);
  if (explicit) return cleanReadableTitle(explicit);

  const ugcElement = asRecord(meta?.ugcElement);
  if (ugcElement) {
    return titleForElementReference(
      pickString(ugcElement, ["elementKey"]),
      pickString(ugcElement, ["kind"]),
    );
  }

  const ugcTextFrame = asRecord(meta?.ugcTextFrame);
  if (ugcTextFrame) {
    const beat = pickString(ugcTextFrame, ["assignedBeat"]) ?? pickString(meta, ["assignedBeat"]);
    return beat ? `${humanizeAssetText(beat)} Text Frame` : "Text Frame";
  }

  if (meta?.assetKind === "ugc-portrait" || artifact.subject === "ugc-portrait") {
    return (
      joinTitleParts([pickString(meta, ["avatarName"]), pickString(meta, ["targetProblem"])]) ||
      artifactTitleFromRawName(artifact.name, artifact.subject)
    );
  }

  if (artifact.subject === "ads:image" || artifact.subject?.startsWith("video-ads:")) {
    return buildAdArtifactDisplayName({
      adType: (artifact.subject ?? "").includes("video") ? "video" : "image",
      metadata: meta,
      fallbackTitle: artifactTitleFromRawName(artifact.name, artifact.subject),
    });
  }

  if (artifact.subject === "ads:video") {
    return buildAdArtifactDisplayName({
      adType: "video",
      metadata: meta,
      fallbackTitle: artifactTitleFromRawName(artifact.name, artifact.subject),
    });
  }

  return artifactTitleFromRawName(artifact.name, artifact.subject);
}

export function readableGeneratedAdTitle(input: {
  type: MediaAssetKind;
  templateName?: string;
  metadata?: Record<string, unknown>;
  category?: string;
}): string {
  const metadata = {
    ...(input.metadata ?? {}),
    ...(input.templateName ? { templateName: input.templateName } : {}),
  };
  const fallbackTitle =
    input.category === "ugc" ? "UGC Portrait" : input.type === "video" ? "Video Ad" : "Image Ad";
  return buildAdArtifactDisplayName({
    adType: input.type,
    metadata,
    fallbackTitle,
  });
}

export function mediaExtensionFromUrl(
  url: string | null | undefined,
  kind: MediaAssetKind,
): string {
  const path = (url ?? "").split(/[?#]/)[0] ?? "";
  const match = path.match(/\.([a-z0-9]{2,5})$/i);
  const ext = match?.[1]?.toLowerCase();
  const imageExts = new Set(["avif", "gif", "jpeg", "jpg", "png", "webp"]);
  const videoExts = new Set(["m4v", "mov", "mp4", "webm"]);
  if (ext && (kind === "video" ? videoExts.has(ext) : imageExts.has(ext))) {
    return ext === "jpeg" ? "jpg" : ext;
  }
  return kind === "video" ? "mp4" : "png";
}

export function safeFilenameBase(title: string | null | undefined): string {
  const base = cleanReadableTitle(title) || "Asset";
  const ascii = base.normalize("NFKD").replace(/[\u0300-\u036f]/g, "");
  return (
    limitTitle(
      ascii
        .replace(/[\\/:*?"<>|]/g, " ")
        .split("")
        .map((char) => (char.charCodeAt(0) < 32 ? " " : char))
        .join("")
        .replace(/\s+/g, " ")
        .replace(/\.+$/g, "")
        .trim(),
      90,
    ) || "Asset"
  );
}

export function buildDownloadFilename(input: {
  title: string;
  url?: string | null;
  kind: MediaAssetKind;
}): string {
  return `${safeFilenameBase(input.title)}.${mediaExtensionFromUrl(input.url, input.kind)}`;
}
